import mammoth from "mammoth";
import JSZip from "jszip";
import * as XLSX from "xlsx";
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from "docx";

export async function docxToHtml(arrayBuffer) {
  const result = await mammoth.convertToHtml({ arrayBuffer });
  return { html: result.value, messages: result.messages || [] };
}

function inlineToRuns(node) {
  const runs = [];
  const walk = (n) => {
    if (n.nodeType === Node.TEXT_NODE) {
      if (n.textContent) {
        runs.push(
          new TextRun({
            text: n.textContent,
            bold: isBold(n.parentNode),
            italics: isItalic(n.parentNode),
            underline: isUnderline(n.parentNode) ? {} : undefined,
          }),
        );
      }
      return;
    }
    if (n.nodeType !== Node.ELEMENT_NODE) return;
    if (n.tagName === "BR") {
      runs.push(new TextRun({ break: 1 }));
      return;
    }
    if (n.tagName === "A") {
      runs.push(
        new TextRun({
          text: n.textContent || "",
          bold: isBold(n),
          italics: isItalic(n),
          underline: {},
          color: "3557E8",
        }),
      );
      return;
    }
    if (["STRONG", "B", "EM", "I", "U", "SPAN", "FONT", "CODE"].includes(n.tagName)) {
      Array.from(n.childNodes).forEach(walk);
      return;
    }
    Array.from(n.childNodes).forEach(walk);
  };
  Array.from(node.childNodes).forEach(walk);
  return runs;
}

function isBold(node) {
  return node?.tagName === "STRONG" || node?.tagName === "B" || (node?.tagName === "SPAN" && /font-weight:\s*bold/i.test(node.style?.fontWeight || ""));
}

function isItalic(node) {
  return node?.tagName === "EM" || node?.tagName === "I";
}

function isUnderline(node) {
  return node?.tagName === "U";
}

function blockToParagraphs(el) {
  const tag = el.tagName;
  if (/^H[1-6]$/.test(tag)) {
    const level = Number(tag.slice(1));
    const heading = {
      1: HeadingLevel.HEADING_1,
      2: HeadingLevel.HEADING_2,
      3: HeadingLevel.HEADING_3,
      4: HeadingLevel.HEADING_4,
      5: HeadingLevel.HEADING_5,
      6: HeadingLevel.HEADING_6,
    }[level];
    return [new Paragraph({ heading, children: inlineToRuns(el) })];
  }
  if (tag === "P") return [new Paragraph({ children: inlineToRuns(el) })];
  if (tag === "UL" || tag === "OL") {
    return Array.from(el.children)
      .filter((li) => li.tagName === "LI")
      .map(
        (li) =>
          new Paragraph({
            bullet: { level: 0 },
            children: inlineToRuns(li),
          }),
      );
  }
  if (tag === "BLOCKQUOTE") {
    return [new Paragraph({ indent: { left: 360 }, children: inlineToRuns(el) })];
  }
  if (tag === "HR") return [new Paragraph({ children: [new TextRun({ text: "————————" })] })];
  if (el.childNodes.length) return [new Paragraph({ children: inlineToRuns(el) })];
  return [];
}

export async function htmlToDocxBlob(html) {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const children = [];
  Array.from(parsed.body.children).forEach((el) => {
    children.push(...blockToParagraphs(el));
  });
  if (!children.length) children.push(new Paragraph({ children: [new TextRun(" ")] }));
  const doc = new Document({ sections: [{ children }] });
  return Packer.toBlob(doc);
}

export function parseWorkbook(arrayBuffer) {
  const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: false });
  const sheets = workbook.SheetNames.map((name) => ({
    name,
    data: XLSX.utils.sheet_to_json(workbook.Sheets[name], {
      header: 1,
      raw: false,
      defval: "",
      blankrows: false,
    }),
  }));
  return { workbook, sheets };
}

export function sheetsToXlsxBlob(sheets) {
  const workbook = XLSX.utils.book_new();
  sheets.forEach((sheet) => {
    const ws = XLSX.utils.aoa_to_sheet(sheet.data.length ? sheet.data : [[""]]);
    XLSX.utils.book_append_sheet(workbook, ws, sheet.name.slice(0, 31) || "Sheet1");
  });
  const out = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    compression: true,
  });
  return new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function sheetToCsv(data) {
  return XLSX.utils.sheet_to_csv(XLSX.utils.aoa_to_sheet(data));
}

export function sheetsToJson(sheets) {
  const result = {};
  sheets.forEach((sheet) => {
    const headers = (sheet.data[0] || []).map((h) => String(h || `列${h + 1}`));
    result[sheet.name] = sheet.data.slice(1).map((row, ri) => {
      const obj = {};
      headers.forEach((h, ci) => {
        obj[h] = row[ci] ?? "";
      });
      return obj;
    });
  });
  return JSON.stringify(result, null, 2);
}

const SLIDE_RE = /^ppt\/slides\/slide(\d+)\.xml$/;
const DRAWING_NS = "http://schemas.openxmlformats.org/drawingml/2006/main";
const REL_NS = "http://schemas.openxmlformats.org/package/2006/relationships";

export async function parsePptx(arrayBuffer) {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const paths = Object.keys(zip.files)
    .filter((p) => SLIDE_RE.test(p))
    .sort((a, b) => Number(a.match(SLIDE_RE)[1]) - Number(b.match(SLIDE_RE)[1]));
  const slides = [];
  for (const path of paths) {
    const xml = await zip.file(path).async("string");
    const doc = new DOMParser().parseFromString(xml, "application/xml");
    const textNodes = Array.from(doc.getElementsByTagNameNS(DRAWING_NS, "t"));
    const blocks = textNodes.map((node, i) => ({
      id: `${path}:${i}`,
      text: node.textContent || "",
    }));
    const images = [];
    const relPath = path.replace("slides/", "slides/_rels/") + ".rels";
    const relFile = zip.file(relPath);
    if (relFile) {
      const relDoc = new DOMParser().parseFromString(await relFile.async("string"), "application/xml");
      Array.from(relDoc.getElementsByTagNameNS(REL_NS, "Relationship")).forEach((rel) => {
        const target = rel.getAttribute("Target") || "";
        if (/\.(png|jpe?g|gif|bmp|webp)$/i.test(target)) {
          images.push(target.replace("../", "ppt/"));
        }
      });
    }
    const imageUrls = [];
    for (const imgPath of images) {
      const imgFile = zip.file(imgPath);
      if (imgFile) imageUrls.push(URL.createObjectURL(await imgFile.async("blob")));
    }
    slides.push({
      number: slides.length + 1,
      blocks,
      imageUrls,
    });
  }
  return { zip, slides };
}

export async function pptxToBlob(zip, editedBlocks) {
  const map = new Map(editedBlocks.map((b) => [b.id, b.text]));
  const paths = Object.keys(zip.files).filter((p) => SLIDE_RE.test(p));
  for (const path of paths) {
    const xml = await zip.file(path).async("string");
    const doc = new DOMParser().parseFromString(xml, "application/xml");
    const nodes = Array.from(doc.getElementsByTagNameNS(DRAWING_NS, "t"));
    let changed = false;
    nodes.forEach((node, i) => {
      const id = `${path}:${i}`;
      const value = map.get(id);
      if (value !== undefined && node.textContent !== value) {
        node.textContent = value;
        changed = true;
      }
    });
    if (changed) {
      zip.file(path, new XMLSerializer().serializeToString(doc));
    }
  }
  return zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 9 },
  });
}

export async function reZipCompress(blob) {
  const zip = await JSZip.loadAsync(blob);
  return zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 9 },
  });
}

export function textToDocxBlob(text) {
  const paragraphs = text
    .split(/\n+/)
    .map((line) => new Paragraph({ children: [new TextRun(line.trim() || " ")] }));
  const doc = new Document({ sections: [{ children: paragraphs }] });
  return Packer.toBlob(doc);
}
